import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  StarIcon,
  CurrencyDollarIcon,
  ShoppingBagIcon,
  AcademicCapIcon,
  HeartIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const PBIS = () => {
  const threeRs = [
    {
      title: "Respectful",
      description: "Students demonstrate respect for themselves, others, and their learning environment through positive interactions and considerate behavior.",
      examples: ["Using kind words", "Listening to others", "Taking care of school property", "Following directions"],
      icon: HeartIcon,
      color: "bg-[#630503]"
    },
    {
      title: "Responsible", 
      description: "Students take ownership of their actions, learning, and contributions to the school community.",
      examples: ["Completing assignments", "Being prepared", "Making good choices", "Helping others"],
      icon: AcademicCapIcon,
      color: "bg-[#8B4513]"
    },
    {
      title: "Reflective",
      description: "Students think about their actions, learn from experiences, and make thoughtful decisions.",
      examples: ["Thinking before acting", "Learning from mistakes", "Setting goals", "Self-evaluation"],
      icon: ChatBubbleLeftRightIcon,
      color: "bg-[#A0522D]"
    }
  ];

  const storeCategories = [
    { name: "School Supplies", price: "5-15 Scholar Dollars", items: "Pencils, erasers, notebooks, stickers" },
    { name: "Fun Activities", price: "20-30 Scholar Dollars", items: "Extra recess, lunch with teacher, computer time" },
    { name: "Special Privileges", price: "40-50 Scholar Dollars", items: "Line leader, office helper, special seating" },
    { name: "Celebration Items", price: "25-35 Scholar Dollars", items: "Certificates, badges, special recognition" }
  ];

  return (
    <main id="main-content" className="bg-white">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-32 pb-24 bg-[#630503] relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1/5 bg-gradient-to-b from-[#630503] to-white"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="mb-16">
              <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] mb-8 rounded-full">
                Positive Behavior Support
              </span>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-8 leading-[1.1] max-w-4xl mx-auto">
                P.S. 11X <span className="font-bold">PBIS Program</span>
              </h1>
              <div className="w-24 h-px bg-white/40 mx-auto mb-8"></div>
              <p className="text-lg lg:text-xl leading-relaxed text-white/95 max-w-3xl mx-auto">
                Building character through our Positive Behavioral Interventions and Supports system, where students earn Scholar Dollars by demonstrating the 3 R's.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Logos Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-12 flex-wrap">
            <img
              src="/assets/images/pbis/ACDP.png"
              alt="ACDP Logo"
              className="h-28 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
            />
            <img
              src="/assets/images/pbis/CS.png"
              alt="Community Schools Logo"
              className="h-28 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
            />
          </div>
        </div>
      </section>

      {/* Video Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Discover Our <span className="font-bold text-[#630503]">PBIS System</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-12">
              Learn about our PBIS store and scholar dollar system through these informative videos!
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {/* PBIS Store Video */}
            <div className="bg-gray-50 rounded-xl p-8 shadow-lg border border-gray-200">
              <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden mb-6 relative">
                <iframe
                  src="https://drive.google.com/file/d/1kvkjPKJBCMrqYhsREyLt8taQ8VhO5_Jn/preview"
                  className="w-full h-full"
                  allow="autoplay"
                  title="PBIS Store Video Trailer"
                ></iframe>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">PBIS Store Video Trailer</h3>
                <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                  Watch our students and staff explain how the Scholar Dollar system works and see the exciting rewards available in our PBIS store.
                </p>
                <a 
                  href="https://drive.google.com/file/d/1kvkjPKJBCMrqYhsREyLt8taQ8VhO5_Jn/view"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-[#630503] hover:bg-[#4a0402] text-white font-bold px-4 py-2 text-xs uppercase tracking-wide transition-all duration-300 shadow-lg hover:shadow-xl rounded-lg"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  Watch Full Video
                </a>
              </div>
            </div>

            {/* YouTube Video */}
            <div className="bg-gray-50 rounded-xl p-8 shadow-lg border border-gray-200">
              <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden mb-6 relative">
                <iframe
                  src="https://www.youtube.com/embed/jaQzLStjLG0"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="PBIS Educational Video"
                ></iframe>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">PBIS Educational Video</h3>
                <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                  Learn more about Positive Behavioral Interventions and Supports and how it benefits our school community.
                </p>
                <a 
                  href="https://www.youtube.com/watch?v=jaQzLStjLG0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-[#630503] hover:bg-[#4a0402] text-white font-bold px-4 py-2 text-xs uppercase tracking-wide transition-all duration-300 shadow-lg hover:shadow-xl rounded-lg"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  Watch on YouTube
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The 3 R's Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              The <span className="font-bold text-[#630503]">3 R's</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Students can earn scholar dollars by following the 3 R's: Respectful, Responsible, and Reflective.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {threeRs.map((r, index) => {
              const IconComponent = r.icon;
              return (
                <div key={index} className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className={`${r.color} p-6 text-white`}>
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                        <IconComponent className="h-7 w-7 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold">{r.title}</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {r.description}
                    </p>
                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider">Examples Include:</h4>
                      <ul className="space-y-2">
                        {r.examples.map((example, exampleIndex) => (
                          <li key={exampleIndex} className="flex items-center text-sm text-gray-600">
                            <div className="w-2 h-2 bg-[#630503] rounded-full mr-3"></div>
                            {example}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Scholar Dollar System */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Scholar Dollar <span className="font-bold text-[#630503]">System</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our reward system recognizes positive behavior and academic achievement through our unique Scholar Dollar currency.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                         {/* How to Earn */}
             <div className="bg-[#630503]/5 rounded-xl p-8 border border-[#630503]/20">
               <div className="flex items-center mb-6">
                 <div className="w-16 h-16 bg-[#630503] rounded-full flex items-center justify-center mr-6">
                   <CurrencyDollarIcon className="h-9 w-9 text-white" />
                 </div>
                 <h3 className="text-2xl font-bold text-gray-900">How to Earn Scholar Dollars</h3>
               </div>
               <div className="space-y-4">
                 <div className="flex items-start">
                   <StarIcon className="h-6 w-6 text-[#630503] mr-3 mt-1 flex-shrink-0" />
                   <div>
                     <h4 className="font-semibold text-gray-900 mb-1">Demonstrating the 3 R's</h4>
                     <p className="text-gray-600 text-sm">Show respectful, responsible, and reflective behavior</p>
                   </div>
                 </div>
                 <div className="flex items-start">
                   <StarIcon className="h-6 w-6 text-[#630503] mr-3 mt-1 flex-shrink-0" />
                   <div>
                     <h4 className="font-semibold text-gray-900 mb-1">Academic Achievement</h4>
                     <p className="text-gray-600 text-sm">Excellence in classwork, homework, and assessments</p>
                   </div>
                 </div>
                 <div className="flex items-start">
                   <StarIcon className="h-6 w-6 text-[#630503] mr-3 mt-1 flex-shrink-0" />
                   <div>
                     <h4 className="font-semibold text-gray-900 mb-1">Helping Others</h4>
                     <p className="text-gray-600 text-sm">Supporting classmates and contributing to school community</p>
                   </div>
                 </div>
                 <div className="flex items-start">
                   <StarIcon className="h-6 w-6 text-[#630503] mr-3 mt-1 flex-shrink-0" />
                   <div>
                     <h4 className="font-semibold text-gray-900 mb-1">Following School Expectations</h4>
                     <p className="text-gray-600 text-sm">Consistently meeting behavioral and academic standards</p>
                   </div>
                 </div>
               </div>
             </div>

            {/* How to Spend */}
            <div className="bg-[#630503]/5 rounded-xl p-8 border border-[#630503]/20">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-[#630503] rounded-full flex items-center justify-center mr-6">
                  <ShoppingBagIcon className="h-9 w-9 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">PBIS Store Rewards</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-6">
                Scholar dollars can be redeemed at our PBIS store for exciting rewards and privileges that make school even more enjoyable.
              </p>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-sm font-medium text-gray-900 mb-2">Store Hours:</p>
                <p className="text-sm text-gray-600">Open during designated times throughout the school week</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Store Categories */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              PBIS Store <span className="font-bold text-[#630503]">Categories</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover the variety of rewards available for purchase with your earned Scholar Dollars.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {storeCategories.map((category, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-[#630503]/10 rounded-full flex items-center justify-center mr-4">
                    <ShoppingBagIcon className="h-6 w-6 text-[#630503]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{category.name}</h3>
                    <p className="text-sm font-medium text-[#630503]">{category.price}</p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {category.items}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PBIS Store Hours */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              PBIS Store <span className="font-bold text-[#630503]">Hours</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Plan your visit to the PBIS store with our scheduled hours and special events.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Store Hours Flyer 1 */}
            <div className="bg-gray-50 rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="mb-6">
                <img
                  src="/assets/images/pbis/PBIS Hr Flyer 3-1.png"
                  alt="PBIS Store Hours Schedule"
                  className="w-full h-auto rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow duration-300"
                  onClick={() => window.open('/assets/images/pbis/PBIS Hr Flyer 3-1.png', '_blank')}
                />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Store Hours Schedule</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Check out the official PBIS store hours and plan your visit to redeem your Scholar Dollars.
                </p>
              </div>
            </div>

            {/* Store Hours Flyer 2 */}
            <div className="bg-gray-50 rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="mb-6">
                <img
                  src="/assets/images/pbis/PBIS Hr Flyer 4-1.png"
                  alt="PBIS Store Hours Information"
                  className="w-full h-auto rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow duration-300"
                  onClick={() => window.open('/assets/images/pbis/PBIS Hr Flyer 4-1.png', '_blank')}
                />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Store Hours Information</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  View additional details about PBIS store operating hours and special shopping times.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <div className="bg-[#630503]/5 rounded-xl p-6 border border-[#630503]/20 max-w-2xl mx-auto">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Important Reminder</h4>
              <p className="text-gray-600 text-sm">
                Remember to bring your Scholar Dollars and be ready to make your selections during store hours. 
                Students must follow all school rules while shopping in the PBIS store.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits & Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              PBIS <span className="font-bold text-[#630503]">Impact</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our PBIS program creates a positive school culture that supports academic success and character development.
            </p>
          </div>

                     <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
             <div className="text-center p-6 bg-gray-50 rounded-xl">
               <div className="w-16 h-16 bg-[#630503] rounded-full flex items-center justify-center mx-auto mb-4">
                 <AcademicCapIcon className="h-8 w-8 text-white" />
               </div>
               <h3 className="text-xl font-bold text-gray-900 mb-3">Academic Success</h3>
               <p className="text-gray-600">Students are motivated to excel academically while developing positive study habits.</p>
             </div>
             
             <div className="text-center p-6 bg-gray-50 rounded-xl">
               <div className="w-16 h-16 bg-[#8B4513] rounded-full flex items-center justify-center mx-auto mb-4">
                 <HeartIcon className="h-8 w-8 text-white" />
               </div>
               <h3 className="text-xl font-bold text-gray-900 mb-3">Character Building</h3>
               <p className="text-gray-600">The 3 R's framework helps students develop essential life skills and values.</p>
             </div>
             
             <div className="text-center p-6 bg-gray-50 rounded-xl">
               <div className="w-16 h-16 bg-[#A0522D] rounded-full flex items-center justify-center mx-auto mb-4">
                 <StarIcon className="h-8 w-8 text-white" />
               </div>
               <h3 className="text-xl font-bold text-gray-900 mb-3">Positive Culture</h3>
               <p className="text-gray-600">Creates an environment where positive behavior is recognized and celebrated.</p>
             </div>
           </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-[#630503]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-light text-white mb-8">
              Join Our <span className="font-bold">PBIS Community</span>
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-12">
              Every student can succeed with our positive behavior support system. Start earning Scholar Dollars today by demonstrating the 3 R's!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://drive.google.com/file/d/1kvkjPKJBCMrqYhsREyLt8taQ8VhO5_Jn/view"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-[#630503] hover:bg-white/90 font-semibold px-8 py-4 text-base transition-colors rounded-lg"
              >
                Watch PBIS Video
              </a>
              <button className="bg-white/10 border border-white/30 text-white hover:bg-white/20 font-semibold px-8 py-4 text-base transition-colors rounded-lg">
                Learn More About Our Program
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default PBIS; 